import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ChevronRight, Plus, SlidersHorizontal } from 'lucide-react';
import { Card, Page } from '../../components/Page';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { useCanWrite } from '../../account/useCanWrite';
import { KEY_TEST_CODES } from '../../data/labCatalog';
import { useLabCatalog, useLabReports, useLabResults } from '../../data/labs';
import { isCritical, isFlagged } from '../../lib/labs';
import { Change, LabStatusBadge, previousResult, resultText } from './shared';

export function LabsPage() {
  const canWrite = useCanWrite();
  const { tests } = useLabCatalog();
  const { reports, isLoading } = useLabReports();
  const { results } = useLabResults();

  const keyTests = KEY_TEST_CODES.map((code) => tests.find((test) => test.code === code)).filter((test) => test && !test.hidden);
  const latestFor = (testId: string) => [...results].reverse().find((result) => result.test_id === testId);

  return (
    <Page
      title="Labs"
      actions={
        canWrite ? (
          <Link
            to="/labs/new"
            className="focus-ring inline-flex min-h-9 items-center gap-1 rounded-lg bg-clinical-primary px-3 text-sm font-semibold text-white hover:bg-clinical-primary-hover"
          >
            <Plus className="h-4 w-4" /> Add report
          </Link>
        ) : null
      }
    >
      {isLoading ? null : reports.length ? (
        <>
          <Card title="Key results">
            <ul className="-mx-4 divide-y divide-clinical-line">
              {keyTests.map((test) => {
                const latest = latestFor(test!.id);
                return (
                  <li key={test!.id}>
                    <Link to={`/labs/tests/${test!.id}`} className="focus-ring flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-clinical-surface">
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{test!.name}</span>
                        {latest ? <Change current={latest} previous={previousResult(results, test!.id, latest.result_date)} /> : <span className="text-xs text-clinical-muted">No results yet</span>}
                      </span>
                      {latest ? (
                        <span className="text-right">
                          <span className="block font-semibold tabular-nums">
                            {resultText(latest)} <span className="text-xs font-normal text-clinical-muted">{latest.unit}</span>
                          </span>
                          <LabStatusBadge status={latest.status} />
                        </span>
                      ) : null}
                      <ChevronRight className="h-4 w-4 shrink-0 text-clinical-muted" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Card>

          <section>
            <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-clinical-muted">Reports</h2>
            <ul className="divide-y divide-clinical-line overflow-hidden rounded-xl border border-clinical-line bg-clinical-card">
              {reports.map((report) => {
                const own = results.filter((result) => result.report_id === report.id);
                const critical = own.filter((result) => isCritical(result.status)).length;
                const flagged = own.filter((result) => isFlagged(result.status)).length;
                return (
                  <li key={report.id}>
                    <Link to={`/labs/${report.id}`} className="focus-ring flex items-center gap-3 px-4 py-3 hover:bg-clinical-surface">
                      <div className="min-w-0 flex-1 text-sm">
                        <p className="font-medium">{format(parseISO(report.result_date), 'd MMMM yyyy')}</p>
                        <p className="text-clinical-muted">
                          {[report.lab_name, `${own.length} ${own.length === 1 ? 'result' : 'results'}`].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      {critical ? (
                        <StatusBadge tone="danger">{critical} very high/low</StatusBadge>
                      ) : flagged ? (
                        <StatusBadge tone="warn">{flagged} outside range</StatusBadge>
                      ) : (
                        <StatusBadge tone="good">All in range</StatusBadge>
                      )}
                      <ChevronRight className="h-4 w-4 shrink-0 text-clinical-muted" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      ) : (
        <EmptyState
          title="No lab reports yet"
          body="Add your monthly results to see trends and flags over time."
          action={
            canWrite ? (
              <Link to="/labs/new" className="font-semibold text-clinical-primary hover:underline">
                Add your first report
              </Link>
            ) : undefined
          }
        />
      )}

      <Link to="/labs/tests" className="focus-ring flex min-h-12 items-center gap-3 rounded-xl border border-clinical-line bg-clinical-card px-4 text-sm font-medium hover:bg-clinical-surface">
        <SlidersHorizontal className="h-5 w-5 text-clinical-primary" />
        <span className="flex-1">Tests & ranges</span>
        <ChevronRight className="h-4 w-4 text-clinical-muted" />
      </Link>
      <p className="px-1 text-xs text-clinical-muted">Ranges are typical ranges; your lab’s printed range takes precedence. You can change any range under Tests & ranges.</p>
    </Page>
  );
}
